import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../utils/api';
import { shouldSuppressErrorForAuth } from '../utils/authErrors';
import { ConfirmModal } from '../components/ConfirmModal';
import { useToast } from '../components/Toast';
import { useCurrentShop } from '../contexts/CurrentShopContext';
import { RefreshWidget } from '../components/RefreshWidget';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useRuntimeSettings } from '../hooks/useRuntimeSettings';
import { isMinor as isMinorFromBirthDate } from '@digitaltattoo/shared-utils';

// ── Types ─────────────────────────────────────────────────────────────────────
interface ActSummary {
  id: string;
  sequence: number;
  status: string;
  demandDetails?: {
    isMinor?: boolean;
    bodyZoneId?: string;
    piercingTypeModelId?: string;
    guardianFirstName?: string;
    guardianLastName?: string;
    jewelry?: { material?: string; option?: string };
    notes?: string;
  } | null;
  bodyZoneName?: string;
  piercingTypeName?: string;
}

interface AppointmentQueueItem {
  id: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientBirthDate?: string;
  isMinor?: boolean | null;
  serviceName: string;
  serviceCategory: string;
  status: 'SCHEDULED' | 'CONFIRMED' | 'CHECKED_IN' | 'IN_PROGRESS';
  scheduledAt: string;
  actualStartTime?: string;
  bookingSource?: string;
  shopName: string;
  practitionerId?: string;
  assignedPractitionerFirstName?: string;
  assignedPractitionerLastName?: string;
  assignedPractitionerArtistName?: string;
  notes?: string;
  /** Present for IN_PROGRESS when the client (or guardian) has not signed the consent yet. */
  canCancelTakeInCharge?: boolean;
  acts: ActSummary[];
}

interface DashboardStats { waiting: number; inProgress: number; totalToday: number; }

// ── Helpers ───────────────────────────────────────────────────────────────────
function getInitials(first: string, last: string) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}
function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
function getWait(scheduledAt: string) {
  const diff = Math.floor((Date.now() - new Date(scheduledAt).getTime()) / 60000);
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `${diff} min`;
  return `${Math.floor(diff / 60)}h${diff % 60 > 0 ? String(diff % 60).padStart(2, '0') : ''}`;
}
// ── Source badge ──────────────────────────────────────────────────────────────
const SOURCE_CFG: Record<string, { label: string; cls: string; icon: string }> = {
  ONLINE:     { label: 'En ligne',   cls: 'bg-blue-50 text-blue-600 border-blue-200',   icon: '🌐' },
  WALK_IN:    { label: 'Boutique',   cls: 'bg-green-50 text-green-700 border-green-200', icon: '🏪' },
  BACKOFFICE: { label: 'Tél / Admin',cls: 'bg-gray-100 text-gray-600 border-gray-200',  icon: '📞' },
};

const SourceBadge: React.FC<{ source?: string }> = ({ source }) => {
  // Normalise case: backend may return lowercase 'online' or mixed case
  const key = (source ?? '').toUpperCase();
  const cfg = SOURCE_CFG[key] ?? SOURCE_CFG.BACKOFFICE;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`}>
      <span>{cfg.icon}</span>
      {cfg.label}
    </span>
  );
};

// ── Assignment badge ──────────────────────────────────────────────────────────
const AssignmentBadge: React.FC<{
  appointmentPractitionerId?: string;
  currentId: string;
  firstName?: string;
  lastName?: string;
  artistName?: string;
}> = ({ appointmentPractitionerId, currentId, firstName, lastName, artistName }) => {
  if (!appointmentPractitionerId)
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border bg-gray-100 text-gray-500 border-gray-200">
        Non assigné
      </span>
    );
  if (appointmentPractitionerId === currentId)
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border bg-primary-50 text-primary-700 border-primary-200">
        ✓ Pour moi
      </span>
    );
  const name = artistName || `${firstName ?? ''} ${lastName ?? ''}`.trim() || 'Autre praticien';
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
      👤 {name}
    </span>
  );
};

// ── Age badge ─────────────────────────────────────────────────────────────────
const AgeBadge: React.FC<{ isMinor: boolean | null | undefined }> = ({ isMinor }) =>
  isMinor === null || isMinor === undefined ? null : isMinor ? (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
      ⚠ Mineur
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border bg-gray-50 text-gray-500 border-gray-200">
      Adulte
    </span>
  );

// ── Acts summary ──────────────────────────────────────────────────────────────
const ActsSummary: React.FC<{ acts: ActSummary[] }> = ({ acts }) => {
  if (!acts.length) return <p className="text-xs text-gray-400 italic">Aucun acte défini</p>;
  return (
    <div className="space-y-1.5">
      {acts.map((act) => {
        const dd = act.demandDetails ?? {};
        const zone = act.bodyZoneName ?? '—';
        const type = act.piercingTypeName ?? '—';
        const jewelry = (dd.jewelry as any);
        const minor = dd.isMinor;
        return (
          <div key={act.id} className="flex items-start gap-2 text-xs">
            <span className="mt-0.5 w-4 h-4 flex-shrink-0 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold text-[10px]">
              {act.sequence}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800">
                {type !== '—' ? type : 'Piercing'}{zone !== '—' ? ` · ${zone}` : ''}
              </p>
              {jewelry && (jewelry.material || jewelry.option) && (
                <p className="text-gray-500">
                  {[jewelry.option, jewelry.material].filter(Boolean).join(' / ')}
                </p>
              )}
              {minor && (
                <p className="text-amber-600 font-medium">
                  ⚠ Mineur
                  {dd.guardianFirstName ? ` — ${dd.guardianFirstName} ${dd.guardianLastName ?? ''}` : ''}
                </p>
              )}
              {dd.notes && <p className="text-gray-400 italic">{dd.notes}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Appointment card ──────────────────────────────────────────────────────────
interface CardProps {
  appointment: AppointmentQueueItem;
  currentPractitionerId: string;
  mode: 'checkedIn' | 'inProgress';
  onAction: (id: string) => void;
  actionLoading: boolean;
  onRequestCancelTakeInCharge?: (a: AppointmentQueueItem) => void;
}

const AppointmentCard: React.FC<CardProps> = ({
  appointment: a,
  currentPractitionerId,
  mode,
  onAction,
  actionLoading,
  onRequestCancelTakeInCharge,
}) => {
  const isCheckedIn  = mode === 'checkedIn';
  const isInProgress = mode === 'inProgress';

  // Minor: from acts demandDetails, or from client birthdate
  const isMinorClient = a.clientBirthDate
    ? isMinorFromBirthDate(a.clientBirthDate)
    : a.isMinor;
  const hasMinorAct   = a.acts.some((act) => act.demandDetails?.isMinor);
  const showMinor     = hasMinorAct ? true : isMinorClient;

  const borderCls = isInProgress
    ? 'border-l-4 border-amber-400'
    : isCheckedIn
    ? 'border-l-4 border-primary-400'
    : 'border-l-4 border-gray-200';

  const avatarCls = isInProgress
    ? 'bg-amber-100 text-amber-600'
    : isCheckedIn
    ? 'bg-primary-100 text-primary-600'
    : 'bg-gray-100 text-gray-400';

  return (
    <div className={`card flex flex-col gap-4 ${borderCls}`}>
      {/* ── Header: avatar + name + status ── */}
      <div className="flex gap-3 items-start">
        <div className={`w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0 text-sm font-bold ${avatarCls}`}>
          {getInitials(a.clientFirstName, a.clientLastName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            {isCheckedIn && <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse flex-shrink-0" />}
            <span className={`text-xs font-bold uppercase tracking-widest ${
              isInProgress ? 'text-amber-500' : isCheckedIn ? 'text-primary-500' : 'text-gray-400'
            }`}>
              {isInProgress ? 'Pris en charge' : isCheckedIn ? 'Arrivé(e)' : a.status === 'CONFIRMED' ? 'Confirmé' : 'Planifié'}
            </span>
          </div>
          <p className="font-bold truncate text-sm">{a.clientFirstName} {a.clientLastName}</p>
          <p className="text-xs text-gray-500 truncate">{a.serviceName}</p>
        </div>
      </div>

      {/* ── Meta pills ── */}
      <div className="flex flex-wrap gap-1.5">
        <SourceBadge source={a.bookingSource} />
        <AssignmentBadge
          appointmentPractitionerId={a.practitionerId}
          currentId={currentPractitionerId}
          firstName={a.assignedPractitionerFirstName}
          lastName={a.assignedPractitionerLastName}
          artistName={a.assignedPractitionerArtistName}
        />
        <AgeBadge isMinor={showMinor} />
      </div>

      {/* ── Acts summary ── */}
      <div className="bg-gray-50 rounded-lg p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
          Actes demandés ({a.acts.length})
        </p>
        <ActsSummary acts={a.acts} />
      </div>

      {/* ── Shop + time + CTA ── */}
      <div className="flex items-end justify-between gap-2 pt-1 border-t border-gray-100">
        <div className="space-y-0.5 text-xs">
          <p className="flex items-center gap-1 text-gray-400">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 4H4v2l8 5 8-5V4zM4 8.5V20h16V8.5l-8 5-8-5z" />
            </svg>
            <span className="font-semibold text-gray-600">{a.shopName}</span>
          </p>
          <p className="text-gray-400 uppercase tracking-wider font-semibold">
            {isInProgress ? 'Démarré à' : isCheckedIn ? 'Arrivée' : 'Prévu à'}
          </p>
          <p className="font-bold text-sm text-gray-800">
            {isInProgress && a.actualStartTime ? formatTime(a.actualStartTime) : formatTime(a.scheduledAt)}
          </p>
          {isCheckedIn && (
            <p className="text-gray-400">Attente : {getWait(a.scheduledAt)}</p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn-primary-sm"
            disabled={actionLoading}
            onClick={() => onAction(a.id)}
          >
            {actionLoading ? (
              <div className="flex items-center gap-1.5">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                <span>...</span>
              </div>
            ) : isInProgress ? 'Continuer' : 'Prendre en charge'}
          </button>
          {isInProgress && a.canCancelTakeInCharge && onRequestCancelTakeInCharge && (
            <button
              type="button"
              className="btn-secondary-sm text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={actionLoading}
              onClick={() => onRequestCancelTakeInCharge(a)}
            >
              Annuler la prise en charge
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const DashboardPractitionerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, ToastComponent } = useToast();
  const { currentShopId, loading: shopLoading } = useCurrentShop();
  const { autoRefreshIntervalSeconds } = useRuntimeSettings();
  const [checkedInQueue,   setCheckedInQueue]  = useState<AppointmentQueueItem[]>([]);
  const [stats,            setStats]           = useState<DashboardStats>({ waiting: 0, inProgress: 0, totalToday: 0 });
  const [currentPractId,   setCurrentPractId]  = useState('');
  const [loading,          setLoading]         = useState(true);
  const [error,            setError]           = useState<string | null>(null);
  const [actionLoading,    setActionLoading]   = useState<string | null>(null);
  const [takeInChargeConfirm, setTakeInChargeConfirm] = useState<AppointmentQueueItem | null>(null);
  const [cancelTakeInChargeConfirm, setCancelTakeInChargeConfirm] =
    useState<AppointmentQueueItem | null>(null);

  const fetchDashboard = useCallback(async (showInitialLoader = false) => {
    if (!currentShopId) return;
    try {
      if (showInitialLoader) {
        setLoading(true);
      }
      const res = await apiClient('/api/practitioner/dashboard');
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        if (data?.code === 'SHOP_SELECTION_REQUIRED') {
          setError('Sélectionnez un shop dans l\'en-tête.');
          return;
        }
        throw new Error('Failed to fetch dashboard');
      }
      const data = await res.json();
      if (data.success) {
        setCheckedInQueue(data.data.checkedInQueue ?? []);
        setStats(data.data.stats);
        setCurrentPractId(data.data.currentPractitionerId ?? '');
        setError(null);
      }
    } catch (err) {
      if (shouldSuppressErrorForAuth(err)) return;
      setError('Impossible de charger le tableau de bord.');
    } finally {
      if (showInitialLoader) {
        setLoading(false);
      }
    }
  }, [currentShopId]);

  const { refreshNow, refreshing, lastRefreshAt } = useAutoRefresh({
    intervalSeconds: autoRefreshIntervalSeconds,
    enabled: !shopLoading && !!currentShopId,
    onRefresh: () => fetchDashboard(false),
  });

  useEffect(() => {
    if (shopLoading) return;
    if (!currentShopId) {
      setLoading(false);
      return;
    }
    void fetchDashboard(true);
  }, [fetchDashboard, currentShopId, shopLoading]);

  useEffect(() => {
    const id = (location.state as { completedAppointmentId?: string } | null)?.completedAppointmentId;
    if (!id) return;
    showToast(
      'Prestation terminée. Le dossier est transmis à la caisse : le client apparaît dans « À encaisser ».',
      'success',
      7000,
    );
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, showToast]);

  const handleTakeInCharge = async (appointmentId: string) => {
    setActionLoading(appointmentId);
    try {
      const res = await apiClient(`/api/practitioner/appointments/${appointmentId}/take-in-charge`, { method: 'POST' });
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e.message);
      }
      setTakeInChargeConfirm(null);
      navigate(`/practitioner/piercing/${appointmentId}/details`);
    } catch (err: any) {
      if (!shouldSuppressErrorForAuth(err)) {
        alert(err.message || 'Erreur lors de la prise en charge');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const waitingClients    = checkedInQueue.filter((a) => a.status === 'CHECKED_IN');
  const inProgressClients = checkedInQueue.filter((a) => a.status === 'IN_PROGRESS');

  const openTakeInChargeConfirm = (appointmentId: string) => {
    const appt = checkedInQueue.find((x) => x.id === appointmentId && x.status === 'CHECKED_IN');
    if (appt) setTakeInChargeConfirm(appt);
  };

  const handleCancelTakeInCharge = async (appointmentId: string) => {
    setActionLoading(appointmentId);
    try {
      const res = await apiClient(`/api/practitioner/appointments/${appointmentId}/cancel-take-in-charge`, {
        method: 'POST',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || 'Impossible d’annuler la prise en charge');
      }
      setCancelTakeInChargeConfirm(null);
      await fetchDashboard(false);
    } catch (err: any) {
      if (!shouldSuppressErrorForAuth(err)) {
        alert(err.message || 'Erreur lors de l’annulation');
      }
    } finally {
      setActionLoading(null);
    }
  };

  if (!shopLoading && !currentShopId) {
    return (
      <main>
        <ToastComponent />
        <div className="card text-center py-10 text-gray-500 border border-dashed border-gray-200">
          <p className="font-medium text-textPrimary">Sélectionnez un shop</p>
          <p className="text-sm mt-1 max-w-md mx-auto">
            Choisissez le shop courant dans l&apos;en-tête pour afficher votre file de clients.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main>
      <ToastComponent />
      <ConfirmModal
        isOpen={!!takeInChargeConfirm}
        title="Prendre en charge ?"
        message={
          takeInChargeConfirm
            ? `Confirmer la prise en charge de ${takeInChargeConfirm.clientFirstName} ${takeInChargeConfirm.clientLastName} (${takeInChargeConfirm.serviceName}) ?`
            : ''
        }
        confirmLabel="Prendre en charge"
        cancelLabel="Annuler"
        confirmVariant="primary"
        isLoading={!!takeInChargeConfirm && actionLoading === takeInChargeConfirm.id}
        onClose={() => {
          if (takeInChargeConfirm && actionLoading === takeInChargeConfirm.id) return;
          setTakeInChargeConfirm(null);
        }}
        onConfirm={() => takeInChargeConfirm && void handleTakeInCharge(takeInChargeConfirm.id)}
      />

      <ConfirmModal
        isOpen={!!cancelTakeInChargeConfirm}
        title="Annuler la prise en charge ?"
        message={
          cancelTakeInChargeConfirm
            ? `${cancelTakeInChargeConfirm.clientFirstName} ${cancelTakeInChargeConfirm.clientLastName} retournera dans la file d’attente (enregistré à l’accueil). Le brouillon de contrat sera supprimé si aucune signature client n’a été enregistrée.`
            : ''
        }
        confirmLabel="Annuler la prise en charge"
        cancelLabel="Retour"
        confirmVariant="warning"
        isLoading={!!cancelTakeInChargeConfirm && actionLoading === cancelTakeInChargeConfirm.id}
        onClose={() => {
          if (cancelTakeInChargeConfirm && actionLoading === cancelTakeInChargeConfirm.id) return;
          setCancelTakeInChargeConfirm(null);
        }}
        onConfirm={() =>
          cancelTakeInChargeConfirm && void handleCancelTakeInCharge(cancelTakeInChargeConfirm.id)
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* Header */}
        <div className="lg:col-span-12 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-display font-bold text-textPrimary">Tableau de bord</h1>
          <RefreshWidget
            intervalSeconds={autoRefreshIntervalSeconds}
            refreshing={refreshing}
            lastRefreshAt={lastRefreshAt}
            onRefresh={() => void refreshNow()}
            disabled={loading || !currentShopId}
          />
        </div>

        {loading && (
          <div className="lg:col-span-12 flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        )}

        {error && (
          <div className="lg:col-span-12 card bg-red-50 border border-red-200 text-red-700 p-4 flex justify-between items-center">
            <span>{error}</span>
            <button className="underline text-sm" onClick={() => void refreshNow()}>Réessayer</button>
          </div>
        )}

        {!loading && !error && (
          <div className="lg:col-span-12 space-y-8">

            {/* ── Stats strip ── */}
            <div className="grid grid-cols-3 gap-4">
              <div className="card bg-surface-container-low text-center py-5">
                <p className="text-4xl font-bold text-primary-500">{String(stats.waiting).padStart(2, '0')}</p>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mt-1">En salle d'attente</p>
              </div>
              <div className="card bg-amber-50 text-center py-5">
                <p className="text-4xl font-bold text-amber-500">{String(stats.inProgress).padStart(2, '0')}</p>
                <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mt-1">Pris en charge</p>
              </div>
              <div className="card text-center py-5">
                <p className="text-4xl font-bold text-gray-700">{String(stats.totalToday).padStart(2, '0')}</p>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mt-1">Total aujourd'hui</p>
              </div>
            </div>

            {/* ── Section 1 : File d'attente (CHECKED_IN + IN_PROGRESS) ── */}
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
                File d'attente praticien
                <span className="ml-auto text-gray-400 font-normal normal-case tracking-normal">
                  {checkedInQueue.length} client{checkedInQueue.length > 1 ? 's' : ''}
                </span>
              </h2>

              {checkedInQueue.length === 0 ? (
                <div className="card text-center py-10 text-gray-400">
                  <svg className="w-10 h-10 mx-auto mb-2 opacity-25" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
                  </svg>
                  <p className="text-sm font-medium">Aucun client enregistré pour le moment</p>
                  <p className="text-xs mt-1">Les clients arrivent après enregistrement par la réception.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {inProgressClients.map((a) => (
                    <AppointmentCard
                      key={a.id}
                      appointment={a}
                      currentPractitionerId={currentPractId}
                      mode="inProgress"
                      onAction={() => navigate(`/practitioner/piercing/${a.id}/details`)}
                      actionLoading={actionLoading === a.id}
                      onRequestCancelTakeInCharge={setCancelTakeInChargeConfirm}
                    />
                  ))}
                  {waitingClients.map((a) => (
                    <AppointmentCard
                      key={a.id}
                      appointment={a}
                      currentPractitionerId={currentPractId}
                      mode="checkedIn"
                      onAction={openTakeInChargeConfirm}
                      actionLoading={actionLoading === a.id}
                    />
                  ))}
                </div>
              )}
            </section>

          </div>
        )}
      </div>
    </main>
  );
};

export default DashboardPractitionerPage;
